import React, { useEffect, useMemo, useState } from "react";
import dotenv from "dotenv";
import parseISO from "date-fns/parseISO";
import differenceInMilliseconds from "date-fns/differenceInMilliseconds";
import Amplify from "aws-amplify";
import { DateUtils } from "@aws-amplify/core";
import { createBrowserHistory } from "history";
import { Route, Router, Switch } from "react-router-dom";
import awsconfig from "../aws-exports";
import { MAX_SIGNV4_OFFSET } from "../constants";
import logger from "../logger";
import BadRequest from "../pages/BadRequest";
import DisabledError from "../pages/DisabledError";
import Landing from "../pages/Landing";
import Marketplace from "../pages/Marketplace";
import NotFound from "../pages/NotFound";
import ServerError from "../pages/ServerError";
import App from "./App";
import Loading from "./Loading";

dotenv.config();

Amplify.configure({
  ...awsconfig,
  aws_appsync_authenticationType: "AWS_IAM"
});

const history = createBrowserHistory();

history.listen(() => {
  window.scrollTo(0, 0);
});

const Root = () => {
  const [clockSkew, setClockSkew] = useState(null);
  const [isError, setIsError] = useState(false);

  const hostname = useMemo(() => {
    if (window.location.hostname === process.env.REACT_APP_CF_HOSTNAME) {
      return null;
    } else {
      return window.location.hostname;
    }
  }, []);

  useEffect(() => {
    setClockOffset();
  }, []);

  async function setClockOffset() {
    try {
      const result = await fetch(`${process.env.REACT_APP_URL_UTILS}/time`);

      if (result.ok) {
        const data = await result.json();
        const servertime = parseISO(data);

        const timediff = Math.abs(differenceInMilliseconds(new Date(), servertime));

        if (timediff > MAX_SIGNV4_OFFSET) {
          DateUtils.setClockOffset(timediff);
        }

        setClockSkew(timediff);
      } else {
        setClockSkew(-1);
        throw new Error(result.statusText);
      }
    } catch (err) {
      logger("Root.setClockOffset.error", err);
      setIsError(true);
    }
  }

  if (!Boolean(clockSkew)) return <Loading fullscreen />;
  if (isError) return <ServerError />;

  return (
    <Router history={history}>
      <Switch>
        {!hostname && <Route exact path="/" component={Landing} />}
        {!hostname && <Route exact path="/marketplace" component={Marketplace} />}
        <Route exact path="/400" component={BadRequest} />
        <Route exact path="/404" component={NotFound} />
        <Route exact path="/500" component={ServerError} />
        <Route exact path="/disabled" component={DisabledError} />
        {hostname ? (
          <Route path="/" component={() => <App hostname={hostname} />} />
        ) : (
          <Route
            path="/:store_slug"
            component={({ match }) => <App store_slug={match.params.store_slug} />}
          />
        )}
      </Switch>
    </Router>
  );
};

export default Root;
